import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip, Button, Collapse, Alert, Grid, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Paper, Tooltip
} from "@mui/material";
import { CallEnd, ExpandMore, ExpandLess, Refresh, FiberManualRecord, OpenInNew } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import {
  aiCallApi, useAICallEvents, useInterval, useNow, formatBdt, formatDuration, dhakaTime, endReasonText,
  toolLabel, isCallActive, reasonText
} from "./useAICalling";
import {
  AICallingPage, SectionCard, Loading, CallStateChip, PurposeChip, OutcomeChip, ConfirmDialog, SipStateChip, TranscriptView
} from "./AICallingComponents";

const MAX_ENDED = 25;

/** Add/replace transcript lines by seq, keep them in order. */
const mergeLines = (lines, line) => {
  const next = lines.filter((l) => l.seq !== line.seq);
  next.push(line);
  return next.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
};

const LiveCallCard = ({ call, now, expanded, onToggle, onHangup }) => {
  const navigate = useNavigate();
  const since = call.answeredAt || call.dialedAt;
  const elapsed = since ? Math.max(0, (now - new Date(since).getTime()) / 1000) : null;
  return (
    <Card variant="outlined" className="aic-card">
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>{call.name || "Unknown member"}</Typography>
            <Typography variant="body2" color="text.secondary">{call.mobile || "—"}</Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <CallStateChip state={call.state} />
            <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {elapsed !== null ? formatDuration(elapsed) : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">{call.answeredAt ? "talking" : call.dialedAt ? "since dialed" : "waiting"}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1, alignItems: "center" }}>
          <PurposeChip purpose={call.purpose} />
          {call.campaignId && (
            <Chip size="small" variant="outlined" label="Campaign" clickable onClick={() => navigate(`/ai-calling/campaigns/${call.campaignId}`)} />
          )}
          {(call.tools || []).map((t, i) => (
            <Tooltip key={`${t.name}-${i}`} title={t.result?.ok === false ? `Failed: ${reasonText(t.result?.error) || "error"}` : "Used by the agent"}>
              <Chip size="small" color={t.result?.ok === false ? "error" : "primary"} label={toolLabel(t.name)} />
            </Tooltip>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, gap: 1, flexWrap: "wrap" }}>
          <Button size="small" onClick={onToggle} endIcon={expanded ? <ExpandLess /> : <ExpandMore />}>
            Transcript ({(call.transcript || []).length})
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate(`/ai-calling/calls/${call.callId}`)}>Details</Button>
            <Button size="small" variant="outlined" color="error" startIcon={<CallEnd />} onClick={() => onHangup(call)}>Hang up</Button>
          </Box>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <TranscriptView lines={call.transcript || []} maxHeight={320} autoScroll emptyText="Nothing said yet." />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const AICallingLive = () => {
  const navigate = useNavigate();
  const now = useNow(1000);
  const [calls, setCalls] = useState({}); // callId → call (+ transcript, tools)
  const [ended, setEnded] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [engineOffline, setEngineOffline] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sip, setSip] = useState(null);
  const [hangup, setHangup] = useState(null);
  const callsRef = useRef(calls);
  const syncTimer = useRef({ id: null });
  const endedIds = useRef(new Set()); // calls we know ended (a slow poll mustn't bring them back)
  const backfilled = useRef(new Set()); // calls whose earlier transcript was fetched

  useEffect(() => {
    callsRef.current = calls;
  }, [calls]);

  /** missed = found gone by a poll (no outcome known): never replaces a call:ended entry. */
  const moveToEnded = useCallback((call, info = {}, missed = false) => {
    endedIds.current.add(call.callId);
    setEnded((list) => {
      if (missed && list.some((c) => c.callId === call.callId)) return list;
      return [
        { ...call, ...info, endedAt: info.endedAt || new Date().toISOString() },
        ...list.filter((c) => c.callId !== call.callId),
      ].slice(0, MAX_ENDED);
    });
  }, []);

  /** Joined mid-call: fetch what was said and done before this page opened (written to the DB live). */
  const backfill = useCallback(async (callId) => {
    if (backfilled.current.has(callId)) return;
    backfilled.current.add(callId);
    const res = await aiCallApi.call(callId);
    if (!res.ok) return;
    const lines = (res.data?.transcript || []).map((t) => ({ seq: t.seq, role: t.role, text: t.text, at: t.at }));
    const tools = (res.data?.call?.toolsUsed || []).map((t) => ({ name: t.name, args: t.args, result: t.result }));
    setCalls((prev) => {
      const call = prev[callId];
      if (!call) return prev;
      const transcript = lines.reduce((acc, line) => mergeLines(acc, line), call.transcript || []);
      return { ...prev, [callId]: { ...call, transcript, tools: (call.tools || []).length >= tools.length ? call.tools : tools } };
    });
  }, []);

  /** Calls in progress from the server; calls that are gone (missed events) move to "Just ended". */
  const loadActive = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const res = await aiCallApi.active();
    setLoading(false);
    if (!res.ok) {
      if (!quiet) setLoadError(res.error);
      return;
    }
    setLoadError("");
    setEngineOffline(!!res.body?.engineOffline);
    if (res.body?.engineOffline) return; // keep what we have; we can't tell what ended
    const list = (Array.isArray(res.data) ? res.data : []).filter((c) => c?.callId && !endedIds.current.has(c.callId));
    const ids = new Set(list.map((c) => c.callId));
    for (const old of Object.values(callsRef.current)) {
      if (!ids.has(old.callId)) moveToEnded(old, { outcome: null }, true);
    }
    setCalls((prev) => {
      const next = {};
      for (const c of list) next[c.callId] = { transcript: [], tools: [], ...prev[c.callId], ...c };
      return next;
    });
    list.filter((c) => c.answeredAt).forEach((c) => backfill(c.callId));
  }, [moveToEnded, backfill]);

  useEffect(() => {
    loadActive();
  }, [loadActive]);
  useEffect(() => {
    const timer = syncTimer.current;
    return () => clearTimeout(timer.id);
  }, []);

  // overview once for the SIP state and channels
  const [channels, setChannels] = useState(null);
  useEffect(() => {
    aiCallApi.overview().then((res) => {
      if (!res.ok) return;
      const engine = res.data?.engine;
      if (engine?.ok) {
        setSip((s) => s || engine.sip || null);
        setChannels(engine.channels || null);
      }
    });
  }, []);

  const syncSoon = useCallback(() => {
    clearTimeout(syncTimer.current.id);
    syncTimer.current.id = setTimeout(() => loadActive(true), 1500);
  }, [loadActive]);

  const onEvent = useCallback(
    (type, data) => {
      if (type === "subscribed") return loadActive(true); // catch up after (re)connecting
      if (type === "sip:status") return setSip(data);
      const callId = data?.callId;
      if (!callId) return undefined;

      if (type === "call:update") {
        if (endedIds.current.has(callId)) return undefined;
        // seen from the start: nothing to backfill
        if (!callsRef.current[callId] && !data.answeredAt) backfilled.current.add(callId);
        setCalls((prev) => ({ ...prev, [callId]: { transcript: [], tools: [], ...prev[callId], ...data } }));
        return undefined;
      }
      if (endedIds.current.has(callId) && type !== "call:ended") return undefined;
      if (type === "call:transcript") {
        const line = { seq: data.seq, role: data.role, text: data.text, at: data.at };
        setCalls((prev) => {
          const call = prev[callId] || { callId, state: "answered", transcript: [], tools: [] };
          return { ...prev, [callId]: { ...call, transcript: mergeLines(call.transcript || [], line) } };
        });
        if (!callsRef.current[callId]) syncSoon(); // joined mid-call: fetch name and number
        return undefined;
      }
      if (type === "call:tool") {
        setCalls((prev) => {
          const call = prev[callId] || { callId, state: "answered", transcript: [], tools: [] };
          return { ...prev, [callId]: { ...call, tools: [...(call.tools || []), { name: data.name, args: data.args, result: data.result }] } };
        });
        return undefined;
      }
      if (type === "call:ended") {
        const call = callsRef.current[callId] || { callId };
        moveToEnded(call, { outcome: data.outcome, durationSec: data.durationSec, costBdt: data.costBdt, endReason: data.endReason, state: "ended" });
        setCalls((prev) => {
          const next = { ...prev };
          delete next[callId];
          return next;
        });
      }
      return undefined;
    },
    [loadActive, moveToEnded, syncSoon]
  );
  const live = useAICallEvents(onEvent);

  // without live events, poll; with them, resync now and then
  useInterval(() => loadActive(true), live.subscribed ? 60000 : 10000);

  const doHangup = async () => {
    const res = await aiCallApi.hangup(hangup.callId);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Hanging up");
    return true;
  };

  const active = Object.values(calls)
    .filter((c) => isCallActive(c.state) || !c.state)
    .sort((a, b) => new Date(b.dialedAt || 0) - new Date(a.dialedAt || 0));

  const liveChip = live.subscribed ? (
    <Chip size="small" color="success" icon={<FiberManualRecord sx={{ fontSize: 12 }} />} label="Live" />
  ) : live.connected ? (
    <Chip size="small" color="warning" label="Connecting…" />
  ) : (
    <Chip size="small" label="Refreshing every 10 s" />
  );

  return (
    <AICallingPage
      title="Live Calls"
      subtitle="Calls in progress, with the live transcript"
      actions={
        <>
          {liveChip}
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => loadActive()} disabled={loading}>Refresh</Button>
        </>
      }
    >
      {engineOffline && (
        <Alert severity="error" sx={{ mb: 2 }}>The calling process isn't running on the server, so there are no live calls.</Alert>
      )}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {sip && sip.state && sip.state !== "registered" && (
        <Alert severity={sip.state === "failing" ? "error" : "warning"} sx={{ mb: 2 }} icon={false}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <span>SIP line:</span>
            <SipStateChip state={sip.state} />
            {sip.lastError && <span>{sip.lastError}</span>}
          </Box>
        </Alert>
      )}
      {live.unavailable && (
        <Alert severity="info" sx={{ mb: 2 }}>Live updates aren't available (not connected). The list refreshes every 10 seconds.</Alert>
      )}

      <SectionCard
        title={`In progress (${active.length})`}
        subtitle={channels ? `Channels in use: ${active.length} / ${channels.max}` : undefined}
      >
        {loading && !active.length ? (
          <Loading text="Loading calls..." />
        ) : active.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No calls right now.</Typography>
        ) : (
          <Grid container spacing={2}>
            {active.map((call) => (
              <Grid item xs={12} md={6} key={call.callId}>
                <LiveCallCard
                  call={call}
                  now={now}
                  expanded={!!expanded[call.callId]}
                  onToggle={() => setExpanded((e) => ({ ...e, [call.callId]: !e[call.callId] }))}
                  onHangup={setHangup}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </SectionCard>

      <SectionCard title="Just ended" subtitle="Calls that ended while this page was open">
        {ended.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nothing yet.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Ended</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right">Talk time</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell>Why it ended</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ended.map((c) => (
                  <TableRow key={c.callId} hover className="aic-row-link" onClick={() => navigate(`/ai-calling/calls/${c.callId}`)}>
                    <TableCell>{dhakaTime(c.endedAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{c.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.mobile || ""}</Typography>
                    </TableCell>
                    <TableCell><PurposeChip purpose={c.purpose} /></TableCell>
                    <TableCell>{c.outcome ? <OutcomeChip outcome={c.outcome} /> : <Typography variant="caption" color="text.secondary">See details</Typography>}</TableCell>
                    <TableCell align="right">{c.durationSec !== undefined ? formatDuration(c.durationSec) : "—"}</TableCell>
                    <TableCell align="right">{c.costBdt !== undefined ? formatBdt(c.costBdt) : "—"}</TableCell>
                    <TableCell>{endReasonText(c.endReason) || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          All calls are in <RouterLink to="/ai-calling/calls">Call history</RouterLink>.
        </Typography>
      </SectionCard>

      <ConfirmDialog
        open={!!hangup}
        title="Hang up this call?"
        confirmText="Hang up"
        confirmColor="error"
        onClose={() => setHangup(null)}
        onConfirm={doHangup}
      >
        {hangup ? `The call with ${hangup.name || hangup.mobile || "this member"} ends right away.` : ""}
      </ConfirmDialog>
    </AICallingPage>
  );
};

export default AICallingLive;
